<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useJobsStore } from './stores';
import { useJobDrafts } from './drafts';
import JobFormModal from './components/JobFormModal.vue';
import JobDetailModal from './components/JobDetailModal.vue';
import ConfirmModal from './components/ConfirmModal.vue';
import { openJobFormNew } from './job-form-modal';
import { openJobDetail } from './job-detail-modal';
import { buildBusyScheduleDays } from '@qjob/shared';
import { getDateKey } from './calendar-helpers';
import { getDraftJobsForCalendar } from './drafts';

const route = useRoute();
const router = useRouter();
const { state, overdueJobs, reload } = useJobsStore();
const { drafts } = useJobDrafts();

const showOverdueAlerts = ref(false);
const draftJobs = computed(() => getDraftJobsForCalendar(drafts.value));
const busyScheduleDays = computed(() =>
  buildBusyScheduleDays([...state.jobs, ...draftJobs.value]).filter((day) => day.dateKey >= getDateKey(new Date())),
);
const notificationCount = computed(() => overdueJobs.value.length + busyScheduleDays.value.length);

const tabLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  jobs: 'All Jobs',
  calendar: 'Calendar',
  schedule: 'คิวงานซ้อน',
  settings: 'Settings',
  logs: 'Logs',
};

const currentTab = computed(() => {
  const name = route.name?.toString() || '';
  if (name === 'dashboard' || name === 'jobs' || name === 'calendar' || name === 'schedule' || name === 'settings' || name === 'logs')
    return name;
  return 'dashboard';
});

const headerTitle = computed(() => tabLabels[currentTab.value] || currentTab.value);

const isBusy = computed(() => state.saving);

function switchTab(tab: string) {
  if (tab === 'jobs') router.push('/jobs');
  else if (tab === 'calendar') router.push('/calendar');
  else if (tab === 'schedule') router.push('/schedule');
  else if (tab === 'settings') router.push('/settings');
  else if (tab === 'logs') router.push('/logs');
  else router.push('/dashboard');
}

function goNewJob() {
  openJobFormNew();
}

function openOverdueJob(job: any) {
  showOverdueAlerts.value = false;
  if (job._rowIndex) openJobDetail(job);
}

function showOverdueJobsOnly() {
  showOverdueAlerts.value = false;
  router.push({ path: '/jobs', query: { overdue: '1' } });
}

function openBusyScheduleDay(dateKey: string) {
  showOverdueAlerts.value = false;
  router.push({ path: '/schedule', query: { date: dateKey } });
}

onMounted(async () => {
  await reload();
});
</script>

<template>
  <div class="flex h-dvh max-h-dvh w-full max-w-full overflow-hidden">
    <!-- Sidebar -->
    <aside
      class="hidden md:flex w-64 shrink-0 bg-white border-r border-slate-200 flex-col h-full min-h-0 transition-all duration-300 z-20"
    >
      <div
        class="h-16 flex-shrink-0 flex items-center justify-center md:justify-start md:px-6 border-b border-slate-100"
      >
        <span class="font-bold text-lg text-primary">💼</span>
        <span class="font-bold text-lg hidden md:block text-slate-800 ml-2">JobBooker</span>
      </div>
      <nav class="flex-1 min-h-0 py-6 flex flex-col gap-2 px-3 overflow-y-auto">
        <button
          @click="switchTab('dashboard')"
          :disabled="isBusy"
          :class="[
            'flex items-center p-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
            currentTab === 'dashboard'
              ? 'bg-blue-50 text-primary font-medium'
              : 'text-slate-500 hover:bg-slate-100',
          ]"
        >
          <i class="ph ph-squares-four text-xl md:mr-3"></i>
          <span class="hidden md:block">Dashboard</span>
        </button>
        <button
          @click="switchTab('jobs')"
          :disabled="isBusy"
          :class="[
            'flex items-center p-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
            currentTab === 'jobs'
              ? 'bg-blue-50 text-primary font-medium'
              : 'text-slate-500 hover:bg-slate-100',
          ]"
        >
          <i class="ph ph-calendar-check text-xl md:mr-3"></i>
          <span class="hidden md:block">All Jobs</span>
        </button>
        <button
          @click="switchTab('calendar')"
          :disabled="isBusy"
          :class="[
            'flex items-center p-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
            currentTab === 'calendar'
              ? 'bg-blue-50 text-primary font-medium'
              : 'text-slate-500 hover:bg-slate-100',
          ]"
        >
          <i class="ph ph-calendar-blank text-xl md:mr-3"></i>
          <span class="hidden md:block">Calendar</span>
        </button>
        <button
          @click="switchTab('schedule')"
          :disabled="isBusy"
          :class="[
            'flex items-center p-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
            currentTab === 'schedule'
              ? 'bg-amber-50 text-amber-800 font-medium'
              : 'text-slate-500 hover:bg-slate-100',
          ]"
        >
          <i class="ph ph-warning-circle text-xl md:mr-3"></i>
          <span class="hidden md:block">คิวงานซ้อน</span>
        </button>
        <div class="mt-auto flex-shrink-0 border-t border-slate-100 pt-2 flex flex-col gap-2">
          <button
            @click="switchTab('logs')"
            :disabled="isBusy"
            :class="[
              'flex items-center p-3 rounded-lg transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed',
              currentTab === 'logs'
                ? 'bg-blue-50 text-primary font-medium'
                : 'text-slate-500 hover:bg-slate-100',
            ]"
          >
            <i class="ph ph-file-text text-xl md:mr-3"></i>
            <span class="hidden md:block">Logs</span>
          </button>
          <button
            @click="switchTab('settings')"
            :disabled="isBusy"
            :class="[
              'flex items-center p-3 rounded-lg transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed',
              currentTab === 'settings'
                ? 'bg-blue-50 text-primary font-medium'
                : 'text-slate-500 hover:bg-slate-100',
            ]"
          >
            <i class="ph ph-gear text-xl md:mr-3"></i>
            <span class="hidden md:block">Settings</span>
          </button>
        </div>
      </nav>
    </aside>

    <!-- Main -->
    <main class="flex-1 min-w-0 flex flex-col h-full overflow-hidden relative pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0">
      <header
        class="h-14 md:h-16 shrink-0 bg-white/90 backdrop-blur border-b border-slate-200 flex items-center justify-between gap-2 px-3 md:px-6 z-10"
      >
        <h1 class="text-base md:text-xl font-semibold text-slate-800 truncate min-w-0">{{ headerTitle }}</h1>
        <div class="flex items-center gap-2 md:gap-4">
          <span
            v-if="state.loading"
            class="hidden sm:flex items-center text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5"
          >
            <span class="inline-block w-3 h-3 border-2 border-blue-700 border-t-transparent rounded-full animate-spin mr-2"></span>
            กำลังซิงค์...
          </span>
          <span
            v-else-if="state.saving"
            class="hidden sm:flex items-center text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5"
          >
            <span class="inline-block w-3 h-3 border-2 border-amber-800 border-t-transparent rounded-full animate-spin mr-2"></span>
            กำลังบันทึก...
          </span>

          <div v-if="notificationCount" class="relative">
            <button
              type="button"
              @click.stop="showOverdueAlerts = !showOverdueAlerts"
              :class="overdueJobs.length ? 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100' : 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'"
              class="relative flex items-center justify-center w-10 h-10 rounded-lg border"
              :title="`แจ้งเตือน ${notificationCount} รายการ`"
            >
              🔔
              <span
                class="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
              >
                {{ notificationCount }}
              </span>
            </button>
            <div
              v-if="showOverdueAlerts"
              @click.stop
              class="absolute right-0 top-full mt-2 w-80 max-w-[92vw] bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden"
            >
              <template v-if="busyScheduleDays.length">
                <div class="px-4 py-3 bg-amber-50 border-b border-amber-100 text-amber-900 font-semibold text-sm">
                  ⚠️ วันมีหลายงาน {{ busyScheduleDays.length }} วัน
                </div>
                <div class="max-h-56 overflow-y-auto">
                  <button
                    v-for="day in busyScheduleDays"
                    :key="day.dateKey"
                    type="button"
                    @click="openBusyScheduleDay(day.dateKey)"
                    class="w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-amber-50"
                  >
                    <div class="font-medium text-slate-800">{{ day.dateKey }} · {{ day.jobs.length }} งาน</div>
                    <div class="text-xs mt-1" :class="day.overlapCount ? 'text-red-700' : day.unknownTimeCount ? 'text-amber-700' : 'text-slate-500'">
                      {{ day.overlapCount ? `เวลาทับ ${day.overlapCount} คู่` : day.unknownTimeCount ? 'มีงานที่ยังไม่ระบุเวลา' : 'หลายงานในวันเดียวกัน' }}
                      <span v-if="day.jobs.some((booking) => booking.job._draftId)" class="font-semibold"> · มีแบบร่าง</span>
                    </div>
                  </button>
                </div>
                <button
                  type="button"
                  @click="switchTab('schedule'); showOverdueAlerts = false"
                  class="w-full px-4 py-2.5 text-sm font-medium text-amber-800 hover:bg-amber-50 border-b border-slate-100"
                >
                  ดูหน้าคิวงานซ้อน
                </button>
              </template>
              <template v-if="overdueJobs.length">
              <div
                class="px-4 py-3 bg-red-50 border-b border-red-100 text-red-800 font-semibold text-sm"
              >
                ⚠️ เกินกำหนดชำระ {{ overdueJobs.length }} รายการ
              </div>
              <div class="max-h-72 overflow-y-auto">
                <button
                  type="button"
                  v-for="job in overdueJobs"
                  :key="(job.JobID as string) || Math.random()"
                  @click="openOverdueJob(job)"
                  class="w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-red-50"
                >
                  <div class="font-medium text-slate-800">{{ job.Client || '-' }}</div>
                  <div class="text-xs text-slate-500 mt-0.5">
                    {{ job['Item / Job Title'] || '-' }}
                  </div>
                  <div class="text-xs text-red-700 mt-1">
                    กำหนดชำระ: {{ job['Payment Due Date'] || '-' }}
                  </div>
                </button>
              </div>
              <button
                type="button"
                @click="showOverdueJobsOnly"
                class="w-full px-4 py-2.5 text-sm font-medium text-red-700 hover:bg-red-50 border-t border-red-100"
              >
                ดูทั้งหมดใน All Jobs
              </button>
              </template>
            </div>
          </div>

          <button
            @click="goNewJob"
            :disabled="isBusy"
            class="shrink-0 bg-primary hover:bg-blue-700 text-white px-3 md:px-4 py-2.5 rounded-lg text-sm font-medium shadow-sm disabled:opacity-60"
          >
            <span class="md:hidden">➕</span>
            <span class="hidden md:inline">➕ New Job</span>
          </button>
          <button
            v-if="drafts.length"
            @click="switchTab('dashboard')"
            class="hidden md:inline-flex bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 px-3 py-2 rounded-lg text-sm font-medium"
          >
            📝 Drafts {{ drafts.length }}
          </button>
        </div>
      </header>

      <div class="flex-1 min-w-0 overflow-y-auto overflow-x-hidden overscroll-contain p-3 md:p-6 bg-slate-50">
        <router-view />
      </div>
    </main>

    <!-- Mobile bottom nav -->
    <nav class="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 px-1 pt-1 safe-bottom">
      <div class="grid grid-cols-6 gap-0.5 max-w-lg mx-auto">
        <button
          @click="switchTab('dashboard')"
          :class="[
            'flex flex-col items-center justify-center min-h-[3rem] rounded-lg text-[10px] leading-tight',
            currentTab === 'dashboard' ? 'text-primary bg-blue-50 font-medium' : 'text-slate-500',
          ]"
        >
          <i class="ph ph-squares-four text-lg"></i>
          Home
        </button>
        <button
          @click="switchTab('jobs')"
          :class="[
            'flex flex-col items-center justify-center min-h-[3rem] rounded-lg text-[10px] leading-tight',
            currentTab === 'jobs' ? 'text-primary bg-blue-50 font-medium' : 'text-slate-500',
          ]"
        >
          <i class="ph ph-calendar-check text-lg"></i>
          Jobs
        </button>
        <button
          @click="switchTab('calendar')"
          :class="[
            'flex flex-col items-center justify-center min-h-[3rem] rounded-lg text-[10px] leading-tight',
            currentTab === 'calendar' ? 'text-primary bg-blue-50 font-medium' : 'text-slate-500',
          ]"
        >
          <i class="ph ph-calendar-blank text-lg"></i>
          Calendar
        </button>
        <button
          @click="switchTab('schedule')"
          :class="[
            'flex flex-col items-center justify-center min-h-[3rem] rounded-lg text-[10px] leading-tight',
            currentTab === 'schedule' ? 'text-amber-800 bg-amber-50 font-medium' : 'text-slate-500',
          ]"
        >
          <i class="ph ph-warning-circle text-lg"></i>
          คิวซ้อน
        </button>
        <button
          @click="switchTab('logs')"
          :class="[
            'flex flex-col items-center justify-center min-h-[3rem] rounded-lg text-[10px] leading-tight',
            currentTab === 'logs' ? 'text-primary bg-blue-50 font-medium' : 'text-slate-500',
          ]"
        >
          <i class="ph ph-file-text text-lg"></i>
          Logs
        </button>
        <button
          @click="switchTab('settings')"
          :class="[
            'flex flex-col items-center justify-center min-h-[3rem] rounded-lg text-[10px] leading-tight',
            currentTab === 'settings' ? 'text-primary bg-blue-50 font-medium' : 'text-slate-500',
          ]"
        >
          <i class="ph ph-gear text-lg"></i>
          Settings
        </button>
      </div>
    </nav>

    <JobDetailModal />
    <JobFormModal />
    <ConfirmModal />
  </div>
</template>
